import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type { City, TrainRoute, SearchFilters } from "../data/types";
import { findRoutesLive } from "../services/routeService";
import { getNearbyPOIs, type POI } from "../services/poiService";
import { getCityById } from "../services/cityStore";
import { reportError } from "../services/errorService";
import { durationMinutes } from "../services/journeyTime";

// ─── Route Search Hook ──────────────────────────────────────────────────
// Pure search state machine. Trip-state orchestration lives in SearchContext.

export interface RouteSearchState {
  fromStation: City | null;
  toStation: City | null;
  routes: TrainRoute[];
  activeRouteIndex: number;
  loading: boolean;
  searchPerformed: boolean;
  searchError: string | null;
  filters: SearchFilters;
  departureTime: string;
  searchDate: string;
  /** Endpoints belonging to the displayed results, independent of edited inputs. */
  resultEndpoints: { from: City; to: City } | null;
  /** POIs found along the active route corridor (for map markers) */
  routePOIs: POI[];
}

export interface RouteSearchActions {
  setFromStation: React.Dispatch<React.SetStateAction<City | null>>;
  setToStation: React.Dispatch<React.SetStateAction<City | null>>;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  setDepartureTime: React.Dispatch<React.SetStateAction<string>>;
  setSearchDate: React.Dispatch<React.SetStateAction<string>>;
  handleSearch: (...args: [from?: City, to?: City, options?: { customDate?: string, customTime?: string }]) => Promise<void>;
  handleSelectRoute: (index: number) => void;
  handleReset: (...args: [City] | [unknown?]) => void;
  /** Clear all search results (routes, POIs, errors). Used when search state becomes stale. */
  clearSearchResults: () => void;
}

export function useRouteSearch(
  travelDate: string,
  setViewMode: React.Dispatch<React.SetStateAction<"search" | "explore">>
): RouteSearchState & RouteSearchActions {
  const [fromStation, setFromStation] = useState<City | null>(null);
  const [toStation, setToStation] = useState<City | null>(null);
  const [allRoutes, setAllRoutes] = useState<TrainRoute[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [resultEndpoints, setResultEndpoints] = useState<{ from: City; to: City } | null>(null);
  const searchController = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({
    sleeperTrainsOnly: false,
    maxDuration: null,
    maxStations: null,
  });
  const [departureTime, setDepartureTime] = useState<string>("08:00"); // default morning
  const [searchDate, setSearchDate] = useState(travelDate);
  useEffect(() => { setSearchDate(travelDate); }, [travelDate]);
  const [routePOIs, setRoutePOIs] = useState<POI[]>([]);

  /** Fetch nearby POIs for every station on a route, deduplicate, return top results. */
  const fetchRoutePOIs = useCallback(async (route: TrainRoute): Promise<POI[]> => {
    const stationIds = route.stationData?.map((s) => s.id) ?? route.stations;
    const coords: Array<{ lat: number; lng: number }> = [];
    for (const id of stationIds) {
      const city = getCityById(id);
      if (city && Number.isFinite(city.latitude) && Number.isFinite(city.longitude)) coords.push({ lat: city.latitude, lng: city.longitude });
    }
    if (coords.length === 0) return [];

    const perStation = await Promise.all(
      coords.map((c) => getNearbyPOIs(c.lat, c.lng, 50, 8).catch(() => [] as POI[]))
    );

    const best = new Map<string, POI>();
    const skipIds = new Set(stationIds);
    for (const pois of perStation) {
      for (const poi of pois) {
        if (skipIds.has(poi.id)) continue;
        const key = poi.id ?? `${poi.latitude},${poi.longitude}`;
        const existing = best.get(key);
        if (!existing || (poi.distanceKm ?? Infinity) < (existing.distanceKm ?? Infinity)) {
          best.set(key, poi);
        }
      }
    }
    return [...best.values()].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity)).slice(0, 8);
  }, []);

  // ─── Derived filtered routes (no API call on filter change) ────

  const routes = useMemo(() => {
    let filtered = [...allRoutes];
    if (filters.sleeperTrainsOnly) {
      filtered = filtered.filter((r) => r.isSleeper);
    }
    if (filters.maxDuration) {
      filtered = filtered.filter(
        (r) => durationMinutes(r.duration) / 60 <= (filters.maxDuration || Infinity)
      );
    }
    if (filters.maxStations) {
      filtered = filtered.filter(
        (r) => r.stations.length <= (filters.maxStations || Infinity)
      );
    }
    return filtered;
  }, [allRoutes, filters]);

  const activeRouteIndex = Math.max(0, routes.findIndex(route => route.id === selectedRouteId));
  const activeRoute = routes[activeRouteIndex];
  useEffect(() => {
    let current = true;
    setRoutePOIs([]);
    if (activeRoute) fetchRoutePOIs(activeRoute).then(pois => { if (current) setRoutePOIs(pois); });
    return () => { current = false; };
  }, [activeRoute, fetchRoutePOIs]);
  useEffect(() => () => searchController.current?.abort(), []);

  // ─── Shared primitives ──────────────────────────────────────────

  /** Clear all search-result state (routes, selection, POIs, errors) */
  const clearSearchResults = useCallback(() => {
    searchController.current?.abort();
    setLoading(false);
    setAllRoutes([]);
    setResultEndpoints(null);
    setSelectedRouteId(null);
    setSearchPerformed(false);
    setSearchError(null);
    setRoutePOIs([]);
  }, []);

  /** Run a route search between two stations and update all result state */
  const executeSearch = useCallback(
    async (from: City, to: City, dateTime: string) => {
      searchController.current?.abort();
      const controller = new AbortController();
      searchController.current = controller;
      setAllRoutes([]);
      setResultEndpoints(null);
      setLoading(true);
      setSearchPerformed(true);
      setSearchError(null);
      try {
        const { routes: found } = await findRoutesLive(from, to, dateTime, 5, controller.signal);
        if (controller.signal.aborted) return;
        setAllRoutes(found);
        setResultEndpoints({ from, to });
        setSelectedRouteId(found[0]?.id ?? null);
      } catch (error) {
        if (controller.signal.aborted) return;
        reportError(error, {
          level: "critical",
          context: { from: from.name, to: to.name },
        });
        setAllRoutes([]);
        const msg = error instanceof Error ? error.message : String(error);
        if (/503|502|500/.test(msg)) {
          setSearchError("The route API is temporarily unavailable — please try again in a few minutes.");
        } else if (/timeout|abort/i.test(msg)) {
          setSearchError("The request timed out. The server may be under heavy load — please try again.");
        } else {
          setSearchError("Could not find routes. Please try again later.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    []
  );

  // ─── Handlers ──────────────────────────────────────────────────

  /** Search for routes. With args: sets from/to state + switches to search view (ghost leg).
   *  Without args: uses current from/to state (SearchPanel). */
  const handleSearch = useCallback(async (from?: City, to?: City, options?: { customDate?: string, customTime?: string }) => {
    const searchFrom = from ?? fromStation;
    const searchTo = to ?? toStation;
    if (!searchFrom || !searchTo) return;
    
    if (from && to) {
      setFromStation(from);
      setToStation(to);
      setViewMode("search");
    }
    
    const date = options?.customDate ?? searchDate;
    const searchTime = options?.customTime ?? (departureTime || "08:00");
    setSearchDate(date);
    
    if (options?.customTime) {
      setDepartureTime(options.customTime);
    }
    
    return executeSearch(searchFrom, searchTo, `${date}T${searchTime}:00`);
  }, [fromStation, toStation, searchDate, departureTime, executeSearch, setViewMode]);

  const handleSelectRoute = useCallback((index: number) => {
    setSelectedRouteId(routes[index]?.id ?? null);
  }, [routes]);

  /** Reset search state. With a Station arg: continue from that station (next leg).
   *  Without arg: full reset (SearchPanel "Reset" button). */
  const handleReset = useCallback((...args: [City] | [unknown?]) => {
    const continueFrom = args.length === 1 && typeof args[0] === "object" && args[0] && "latitude" in args[0] ? args[0] as City : undefined;
    searchController.current?.abort();
    setLoading(false);
    if (continueFrom) {
      // "Continue from" — keep routes/POIs visible on the map until the
      // next search naturally replaces them.  Only update search inputs.
      setFromStation(continueFrom);
      setToStation(null);
      setSearchError(null);
    } else {
      // Full reset — clear everything
      clearSearchResults();
      setFromStation(null);
      setToStation(null);
    }
  }, [clearSearchResults]);

  return {
    fromStation,
    toStation,
    routes,
    activeRouteIndex,
    loading,
    searchPerformed,
    searchError,
    filters,
    departureTime,
    searchDate,
    resultEndpoints,
    routePOIs,

    setFromStation,
    setToStation,
    setFilters,
    setDepartureTime,
    setSearchDate,
    handleSearch,
    handleSelectRoute,
    handleReset,
    clearSearchResults,
  };
}
